import { screenSizes, styled } from '@aether/styles'
import { Media } from '@aether/components'
import {
  InsiderPage,
  MediaFieldType,
  SanityImageMediaPayload as SanityImage,
} from '@aether/models'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'

interface ImageCarouselProps {
  imageCarousel: InsiderPage['imageCarousel']
}

const Root = styled('section', {
  width: '100%',
  maxWidth: '100vw',
  overflow: 'hidden',
  margin: '0 auto $32',
})

const Slider = styled('div', {
  padding: 0,
})

const CarouselContainer = styled('div', {
  overflow: 'hidden',
})

const ImageContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const Title = styled('h2', {
  $aetherFont: 'heading02',
  paddingTop: 0,
  maxWidth: '$containerSizeM',
  margin: '0 auto $48',
  paddingInline: '$24',

  '@md': {
    marginBottom: '$64',
    paddingInline: 0,
  },
})

type ImageOrientation = 'horizontal' | 'vertical'

const DURATION_FOR_ONE_IMAGE = 7000

const getImageOrientation = (media?: MediaFieldType): ImageOrientation => {
  if (!media?.mediaPayload || !('image' in media.mediaPayload)) {
    return 'horizontal'
  }

  const { width, height } = (media.mediaPayload as SanityImage).image
  return width >= height ? 'horizontal' : 'vertical'
}

export const ImageCarousel = ({ imageCarousel }: ImageCarouselProps) => {
  const duration = DURATION_FOR_ONE_IMAGE * (imageCarousel?.images?.length || 1)

  const animation = { duration, easing: (t: number) => t }
  const images = imageCarousel?.images
    ? [...imageCarousel.images, ...imageCarousel.images].map((image) => {
        return { ...image, orientation: getImageOrientation(image.media) }
      })
    : []

  const count = images.length
  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    renderMode: 'performance',
    drag: false,
    slides: () =>
      images.map((_, index) => ({
        size: images[index]?.orientation === 'horizontal' ? 1 : 0.79,
        spacing: 0.08,
      })),
    breakpoints: {
      [`(min-width: ${screenSizes.md}px)`]: {
        slides: () =>
          images.map((_, index) => ({
            size: images[index]?.orientation === 'horizontal' ? 0.35 : 0.23,
            spacing: 0.06,
          })),
      },
    },
    mode: 'free',
    created(s) {
      s.moveToIdx(count, true, animation)
    },
    updated(s) {
      s.moveToIdx(s.track.details.abs + count, true, animation)
    },
    animationEnded(s) {
      s.moveToIdx(s.track.details.abs + count, true, animation)
    },
  })

  if (!images.length) return null

  return (
    <Root>
      {imageCarousel?.title && <Title>{imageCarousel.title}</Title>}

      <CarouselContainer>
        <Slider ref={sliderRef} className="keen-slider">
          {images.map((item, index) => {
            if (!item?.media?.mediaPayload) return null

            return (
              <ImageContainer key={`${index}`} className="keen-slider__slide">
                <Media
                  {...item.media}
                  objectFit="contain"
                  sizes={{ default: '100vw', md: '35vw' }}
                />
              </ImageContainer>
            )
          })}
        </Slider>
      </CarouselContainer>
    </Root>
  )
}
